import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Zap, Clock, AlertTriangle, Check, X, Users, Ban,
  TrendingUp, ChevronDown, Loader2, Eye, Bell,
} from "lucide-react";
import { Button } from "../components/ui/button";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtTime = (t) => {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  const s = h >= 12 ? "pm" : "am";
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return m === 0 ? `${h12}${s}` : `${h12}:${String(m).padStart(2, "0")}${s}`;
};

// ═══════════════════════════════════
//  GAP REFILL PANEL (inside lesson detail)
// ═══════════════════════════════════
export function GapRefillPanel({ gap, pupils, onClose, onFilled }) {
  const [selectedPupil, setSelectedPupil] = useState("");
  const [filling, setFilling] = useState(false);

  const fillGap = async () => {
    if (!selectedPupil) { toast.error("Select a pupil"); return; }
    setFilling(true);
    try {
      const { data } = await axios.post(
        `${API}/api/gaps/${gap.id}/fill`,
        { pupil_id: selectedPupil },
        { withCredentials: true }
      );
      toast.success(`Slot filled! ${data.pupil_name} booked — £${data.amount}`);
      onFilled?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to fill gap");
    }
    setFilling(false);
  };

  const blockGap = async () => {
    try {
      await axios.post(`${API}/api/gaps/${gap.id}/block`, {}, { withCredentials: true });
      toast.success("Slot blocked");
      onFilled?.();
    } catch (err) { toast.error("Failed"); }
  };

  const ignoreGap = async () => {
    try {
      await axios.post(`${API}/api/gaps/${gap.id}/ignore`, {}, { withCredentials: true });
      toast.success("Gap dismissed");
      onFilled?.();
    } catch (err) { toast.error("Failed"); }
  };

  return (
    <div data-testid="gap-refill-panel" className="p-5 space-y-4">
      <div className="w-10 h-1 bg-slate-200 dark:bg-slate-600 rounded-full mx-auto" />

      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-12 h-12 rounded-full bg-amber-100 dark:bg-amber-900/20 flex items-center justify-center">
          <Zap className="w-6 h-6 text-amber-500" />
        </div>
        <div className="flex-1">
          <h2 className="text-lg font-extrabold text-slate-900 dark:text-white">Fill This Slot</h2>
          <p className="text-sm text-slate-400">
            {new Date(gap.date + "T00:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
            {" "}{fmtTime(gap.start_time)}–{fmtTime(gap.end_time)}
          </p>
        </div>
        <button onClick={onClose} className="p-2 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-700"><X className="w-5 h-5 text-slate-400" /></button>
      </div>

      {/* Value */}
      <div className="flex items-center gap-4 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 rounded-2xl p-3">
        <div>
          <p className="text-xs text-amber-600 font-bold">Potential Revenue</p>
          <p className="text-2xl font-extrabold text-amber-700 dark:text-amber-400">£{(gap.potential_value || 0).toFixed(0)}</p>
        </div>
        <div className="flex-1 text-right">
          <p className="text-xs text-slate-400">Source: <span className="font-bold capitalize">{gap.source}</span></p>
          {gap.original_pupil && <p className="text-xs text-slate-400">Was: {gap.original_pupil}</p>}
        </div>
      </div>

      {/* Pupil Selector */}
      <div>
        <p className="text-sm font-bold text-slate-900 dark:text-white mb-2">Choose Pupil</p>
        <select data-testid="gap-pupil-select" value={selectedPupil}
          onChange={e => setSelectedPupil(e.target.value)}
          className="w-full border border-slate-200 dark:border-slate-600 rounded-xl px-4 py-3 text-base font-medium bg-white dark:bg-slate-700">
          <option value="">Select a pupil...</option>
          {(pupils || []).map(p => (
            <option key={p.id} value={p.id}>
              {p.first_name} {p.last_name} {p.phone ? `(${p.phone})` : ""}
            </option>
          ))}
        </select>
      </div>

      {/* Actions */}
      <div className="space-y-2">
        <Button data-testid="gap-fill-btn" onClick={fillGap} disabled={!selectedPupil || filling}
          className="w-full h-12 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl font-bold">
          {filling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Book Pupil Into Slot"}
        </Button>
        <div className="grid grid-cols-2 gap-2">
          <button data-testid="gap-block-btn" onClick={blockGap}
            className="py-2.5 rounded-xl border border-red-200 dark:border-red-800 text-red-500 text-xs font-bold hover:bg-red-50 dark:hover:bg-red-900/10">
            Block Slot
          </button>
          <button data-testid="gap-ignore-btn" onClick={ignoreGap}
            className="py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-slate-400 text-xs font-bold hover:bg-slate-50 dark:hover:bg-slate-700">
            Dismiss
          </button>
        </div>
      </div>

      {/* Activity */}
      {gap.activity?.length > 0 && (
        <div className="pt-2 border-t border-slate-100 dark:border-slate-700">
          <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2">Activity</p>
          {gap.activity.slice(-3).map((a, i) => (
            <div key={i} className="flex items-center gap-2 py-1">
              <div className="w-1.5 h-1.5 rounded-full bg-blue-400" />
              <p className="text-[11px] text-slate-500">{a.message}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ═══════════════════════════════════
//  ACTIVITY FEED
// ═══════════════════════════════════
export function GapActivityFeed() {
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/gaps/activity`, { withCredentials: true });
        setActivity(data.activity || []);
      } catch {}
      setLoading(false);
    })();
  }, []);

  if (loading) return null;
  if (activity.length === 0) return null;

  const iconMap = {
    gap_detected: <Zap className="w-3.5 h-3.5 text-amber-500" />,
    slot_filled: <Check className="w-3.5 h-3.5 text-emerald-500" />,
    gap_blocked: <Ban className="w-3.5 h-3.5 text-red-400" />,
  };

  return (
    <div data-testid="gap-activity-feed" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Bell className="w-4 h-4 text-amber-500" />
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Gap Activity</p>
      </div>
      <div className="space-y-2 max-h-48 overflow-y-auto">
        {activity.slice(0, 10).map((a, i) => (
          <div key={i} className="flex items-start gap-2.5">
            <div className="mt-1">{iconMap[a.type] || <Eye className="w-3.5 h-3.5 text-slate-400" />}</div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-bold text-slate-700 dark:text-slate-300">{a.title}</p>
              <p className="text-[10px] text-slate-400 truncate">{a.message}</p>
            </div>
            <p className="text-[9px] text-slate-300 flex-shrink-0">
              {new Date(a.created_at).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}


// ═══════════════════════════════════
//  GAP METRICS CARD
// ═══════════════════════════════════
export function GapMetrics() {
  const [metrics, setMetrics] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/api/gaps/metrics`, { withCredentials: true });
        setMetrics(data);
      } catch {}
    })();
  }, []);

  if (!metrics || metrics.total_gaps === 0) return null;

  return (
    <div data-testid="gap-metrics" className="bg-white dark:bg-slate-800 rounded-2xl border border-slate-200 dark:border-slate-700 p-4">
      <div className="flex items-center gap-2 mb-3">
        <TrendingUp className="w-4 h-4 text-emerald-500" />
        <p className="text-xs font-bold text-slate-400 uppercase tracking-wider">Gap Recovery</p>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div className="text-center">
          <p className="text-xl font-extrabold text-emerald-600">£{(metrics.recovered_week || 0).toFixed(0)}</p>
          <p className="text-[9px] text-slate-400 font-bold">Recovered</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-extrabold text-red-500">£{(metrics.lost_month || 0).toFixed(0)}</p>
          <p className="text-[9px] text-slate-400 font-bold">At Risk</p>
        </div>
        <div className="text-center">
          <p className="text-xl font-extrabold text-blue-600">{metrics.fill_rate || 0}%</p>
          <p className="text-[9px] text-slate-400 font-bold">Fill Rate</p>
        </div>
      </div>
      <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100 dark:border-slate-700">
        <p className="text-[10px] text-slate-400"><span className="font-bold text-slate-600 dark:text-slate-300">{metrics.open_gaps}</span> open gaps</p>
        <p className="text-[10px] text-slate-400"><span className="font-bold text-slate-600 dark:text-slate-300">{metrics.gaps_this_week}</span> this week</p>
        <p className="text-[10px] text-slate-400"><span className="font-bold text-slate-600 dark:text-slate-300">{metrics.filled_month}</span> filled</p>
      </div>
    </div>
  );
}
